import type React from 'react';
import type {
	AgeRange,
	AppData,
	Employee,
	EmployeeCounts,
	EmployeeSummary,
	Quote,
	SalaryRange,
	SavingPercentages
} from '@/types/app';

/**
 * Reads the current policy's sick-leave-short-term premium rate percentage.
 * Single source of truth for the field path so the nested `current_policy`
 * shape (premium as a sibling of the `coverage` block) is read consistently.
 * @returns the premium rate percentage, or `undefined` when none is set
 */
export const getCurrentPremiumRatePercentage = (
	appData: AppData | null | undefined
): number | undefined => {
	const value = appData?.current_policy?.sick_leave_short_term?.premium_rate_percentage;
	return typeof value === 'number' ? value : undefined;
};

/**
 * Gets saving percentage range from quotes data
 * @param data - Array of quote objects
 * @returns Object with max and min saving percentages
 */
export const getSavingPercentages = (data: Quote[]): SavingPercentages => {
	const savingPercentages = data
		.filter(
			(item): item is Quote & { saving_percentage: number } =>
				item.saving_percentage != null &&
				item.saving_percentage >= 0 &&
				item.saving_percentage <= 99
		)
		.map((item) => item.saving_percentage);
	if (savingPercentages.length === 0) {
		return { max: 0, min: 0 };
	}
	const max = Math.round(Math.max(...savingPercentages));
	const min = Math.round(Math.min(...savingPercentages));
	return { max, min };
};

/**
 * Filters out quotes with unavailable premium rate percentage (null, undefined, or zero)
 * @param data - Array of quote objects
 * @returns Filtered array of quotes with valid premium rates
 */
export const filterQuotesWithSavingPercentageZero = (data: Quote[]): Quote[] => {
	return data.filter(
		(item) => item.premium_rate_percentage !== 0 && item.premium_rate_percentage != null
	);
};

/**
 * Sorts quotes by saving percentage in descending order, with 100% last
 * @param data - Array of quote objects
 * @returns Sorted array of quotes with originalIndex preserved
 */
export const sortQuotesBySavingPercentage = (data: Quote[]): Quote[] => {
	return data
		.map((item, index) => ({ ...item, originalIndex: index }))
		.sort((a, b) => {
			if (a.saving_percentage === 100) return 1;
			if (b.saving_percentage === 100) return -1;
			return (b.saving_percentage ?? 0) - (a.saving_percentage ?? 0);
		});
};

/**
 * Sorts quotes by premium percentage in ascending order, with unavailable quotes last
 * @param data - Array of quote objects
 * @returns Sorted array of quotes with originalIndex preserved
 */
export const sortQuotesByPremiumPercentage = (data: Quote[]): Quote[] => {
	return data
		.map((item, index) => ({ ...item, originalIndex: index }))
		.sort((a, b) => {
			// Move unavailable quotes (0, null, undefined) to the end
			const aUnavailable = a.premium_rate_percentage === 0 || a.premium_rate_percentage == null;
			const bUnavailable = b.premium_rate_percentage === 0 || b.premium_rate_percentage == null;
			if (aUnavailable && !bUnavailable) return 1;
			if (bUnavailable && !aUnavailable) return -1;
			// Both available or both unavailable: sort by premium rate ascending
			return (a.premium_rate_percentage ?? 0) - (b.premium_rate_percentage ?? 0);
		});
};

/**
 * Sorts quotes by insurer name alphabetically, with unavailable quotes last
 * @param data - Array of quote objects
 * @returns Sorted array of quotes with originalIndex preserved
 */
export const sortQuotesByInsurer = (data: Quote[]): Quote[] => {
	return data
		.map((item, index) => ({ ...item, originalIndex: index }))
		.sort((a, b) => {
			// Move unavailable quotes (0, null, undefined) to the end
			const aUnavailable = a.premium_rate_percentage === 0 || a.premium_rate_percentage == null;
			const bUnavailable = b.premium_rate_percentage === 0 || b.premium_rate_percentage == null;
			if (aUnavailable && !bUnavailable) return 1;
			if (bUnavailable && !aUnavailable) return -1;

			// Both available or both unavailable: sort by insurer name alphabetically
			const nameA = (a.insurer_name || '').toLowerCase();
			const nameB = (b.insurer_name || '').toLowerCase();
			return nameA.localeCompare(nameB, 'nl');
		});
};

/**
 * Checks if a quote is unavailable based on premium rate and saving percentage
 * @param quote - Quote object to check
 * @returns True if quote is unavailable
 */
export const isUnavailable = (quote: Quote): boolean => {
	return (
		quote.premium_rate_percentage === 0 ||
		quote.premium_rate_percentage === null ||
		quote.premium_rate_percentage === undefined ||
		quote.premium_rate_percentage === 0.0 ||
		quote.saving_percentage === 100
	);
};

/**
 * A carousel quote/provider price is "unavailable" when it is missing or non-positive.
 * Shared by all product-type carousel cards (Quote, Provider) so €0 / null prices are
 * treated identically: greyed out, labelled "no proposal", and non-selectable.
 * @param price - The numeric price/rate, or null/undefined when absent
 * @returns True when the price cannot be offered to the user
 */
export const isPriceUnavailable = (price: number | null | undefined): boolean =>
	price == null || !Number.isFinite(price) || price <= 0;

/**
 * Cheapest first, with unpriced quotes pushed to the end rather than sorted as 0.
 * Shared by every surface that lists insurance quotes so the order never differs
 * between the customer flow and the request overview.
 */
export const sortQuotesByPremiumRate = <T extends { premium_rate_percentage: number }>(
	quotes: T[]
): T[] => {
	const sortValue = (quote: T): number =>
		quote.premium_rate_percentage > 0 ? quote.premium_rate_percentage : Number.POSITIVE_INFINITY;
	return [...quotes].sort((a, b) => sortValue(a) - sortValue(b));
};

/** Cheapest first for occupational-health providers, whose rate is a string. */
export const sortProvidersByRate = <T extends { rate: string }>(quotes: T[]): T[] => {
	const sortValue = (quote: T): number => {
		const rate = Number.parseFloat(quote.rate);
		return Number.isFinite(rate) && rate > 0 ? rate : Number.POSITIVE_INFINITY;
	};
	return [...quotes].sort((a, b) => sortValue(a) - sortValue(b));
};

/**
 * Generates age range options for employee forms
 * @returns Array of age range objects
 */
export const generateAges = (): AgeRange[] => {
	const ageRanges: AgeRange[] = [
		{ value: '23', display: '20 tot 25' },
		{ value: '28', display: '25 tot 30' },
		{ value: '33', display: '30 tot 35' },
		{ value: '38', display: '35 tot 40' },
		{ value: '43', display: '40 tot 45' },
		{ value: '48', display: '45 tot 50' },
		{ value: '53', display: '50 tot 55' },
		{ value: '58', display: '55 tot 60' },
		{ value: '63', display: '60 tot 65' }
	];

	return ageRanges;
};

/**
 * Generates salary range options for employee forms
 * @returns Array of salary range objects
 */
export const generateSalaryRanges = (): SalaryRange[] => {
	const ranges: SalaryRange[] = [];
	ranges.push({ value: '< 5.000', display: '< 5k' });
	for (let i = 5000; i <= 200000; i += 5000) {
		ranges.push({
			value: i.toString(),
			display: `€ ${i.toLocaleString('nl-NL')}`
		});
	}
	ranges.push({ value: '> 200.000', display: '> 200k' });
	return ranges;
};

/**
 * Compares two objects for equality using JSON stringification
 * @param obj1 - First object to compare
 * @param obj2 - Second object to compare
 * @returns True if objects are equal
 */
export const isEqual = (
	obj1: Record<string, unknown> | null,
	obj2: Record<string, unknown> | null
): boolean => {
	if (obj1 === null && obj2 === null) return true;
	if (obj1 === null || obj2 === null) return false;
	return JSON.stringify(obj1) === JSON.stringify(obj2);
};

/**
 * Gets employee count statistics including gender distribution
 * @param employees - Array of employee summary objects
 * @returns Object with total count and gender counts
 */
export const getEmployeeCounts = (employees: EmployeeSummary[]): EmployeeCounts => {
	const count = employees.length;
	const maleCount = employees.filter((emp) => emp.gender === 'male').length;
	const femaleCount = employees.filter((emp) => emp.gender === 'female').length;
	return { count, maleCount, femaleCount };
};

/**
 * Calculates total wage from employee data and formats with German locale
 * @param employees - Array of employee summary objects
 * @returns Formatted total wage string
 */
export const getTotalWage = (employees: EmployeeSummary[]): string => {
	const total = employees.reduce((total, emp) => {
		const wage =
			typeof emp.wage === 'string'
				? Number.parseFloat(emp.wage.replace(/\./g, '').replace(',', '.'))
				: emp.wage;
		return total + (wage || 0);
	}, 0);
	return new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(
		Math.round(total ?? 0)
	);
};

/**
 * Normalises employees coming from PDF/file extraction into the shape the editable
 * employee grid expects: missing fields become empty strings (so the grid flags them
 * as required) and the wage is rounded. Shared by the upload-import flow and the
 * panel pre-fill that reuses an existing extraction (issue #927).
 * @param employees - Employees as returned by the extraction endpoint
 * @returns Employees ready to feed into `premiumVarManagement.employees.replace`
 */
export const prepareExtractedEmployees = (employees: Employee[]): Employee[] =>
	employees.map((employee) => ({
		...employee,
		name: employee.name || '',
		gender: employee.gender || '',
		birth_date: employee.birth_date || '',
		wage: Math.round(employee.wage || 0),
		contract_type: employee.contract_type || ''
	}));

/**
 * Validates if a string represents a valid number
 * @param value - String value to validate
 * @returns True if value is a valid number
 */
export const isValidNumber = (value: string): boolean => /^\d+(\.\d+)?$/.test(value);

/**
 * Formats wage value with Dutch locale
 * @param value - Wage value as string or number
 * @returns Formatted wage string or empty string for falsy values
 */
export const formatWage = (value: string | number): string => {
	if (value === '' || value === null || value === undefined) return '';
	const number = Math.round(Number.parseFloat(value.toString()));
	return new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 0 }).format(number);
};

/**
 * Formats a numeric value to one decimal place
 * @param value - Value to format (string, number, null, or undefined)
 * @returns Formatted string with one decimal place, or empty string for invalid/falsy values
 */
export const formatToOneDecimal = (value: string | number | null | undefined): string => {
	// Check for null/undefined/empty string, but allow 0
	if (value === null || value === undefined || value === '') return '';
	// Normalize comma to period for parsing (support both input formats)
	const normalizedValue = typeof value === 'string' ? value.replace(',', '.') : value;
	const numValue = Number(normalizedValue);
	if (Number.isNaN(numValue)) return '';
	// Output with period as decimal separator
	return numValue.toFixed(1);
};

/**
 * Extracts year from date string in YYYY-MM-DD format
 * @param dateString - Date string to extract year from
 * @returns Year string or empty string if invalid
 */
export const extractYearFromDate = (dateString: string): string => {
	if (!dateString) return '';
	// Extract year from YYYY-MM-DD format
	const parts = dateString.split('-');
	if (parts.length !== 3) return '';
	const year = parts[0];
	return /^\d{4}$/.test(year) ? year : '';
};

/**
 * Converts year string to YYYY-01-01 date format for API
 * @param year - Year string (YYYY format)
 * @returns Date string in YYYY-01-01 format or empty string if invalid
 */
export const convertYearToDate = (year: string): string => {
	if (year?.length !== 4) return '';
	// Convert year to YYYY-01-01 format for API
	return `${year}-01-01`;
};

/**
 * Validates if year is within valid range for employee birth years
 * @param year - Year string to validate
 * @returns True if year is valid (1950-2012)
 */
export const isValidYear = (year: string): boolean => {
	if (year?.length !== 4) return false;
	const yearNum = Number.parseInt(year, 10);
	// Valid year range: 1950 to 2012 for employee birth years
	return yearNum >= 1950 && yearNum <= 2012;
};

/**
 * Converts absence values to rates by year
 * @param absenceValues - Object with vz1, vz2, vz3 absence values
 * @returns Object mapping years to absence rates
 */
export const convertAbsenceValuesToRatesByYear = (absenceValues: {
	vz1?: string | number;
	vz2?: string | number;
	vz3?: string | number;
}): Record<number, number> => {
	const currentYear = getCurrentAbsenceBaseYear();
	// Normalize comma to period for Dutch decimal notation support before parsing
	const normalizeValue = (val?: string | number): number => {
		const strVal = val?.toString().replace(',', '.') || '0';
		return Number.parseFloat(strVal);
	};
	return {
		[currentYear - 1]: normalizeValue(absenceValues.vz1),
		[currentYear - 2]: normalizeValue(absenceValues.vz2),
		[currentYear - 3]: normalizeValue(absenceValues.vz3)
	};
};

/**
 * Gets current absence report base year
 * Returns current year until September 30, next year from October 1
 * @returns Base year for absence reporting
 */
export const getCurrentAbsenceBaseYear = (): number => {
	const currentYear = new Date().getFullYear();
	const currentMonth = new Date().getMonth() + 1;
	const returnYear = currentMonth >= 10 ? currentYear + 1 : currentYear;
	return returnYear;
};

// Initials formatting utility functions
export const filterInitialsInput = (value: string): string => {
	// Only allow Unicode letters and spaces (supports diacritics like É, Å, Ł)
	return value.replace(/[^\p{L}\s]/gu, '');
};

export const formatInitialsWithSpaces = (value: string): string => {
	// Remove existing spaces and add space between each letter
	const lettersOnly = value.replace(/\s/g, '');
	return lettersOnly.split('').join(' ');
};

/**
 * Adds a value to an array if it's not already present and returns a sorted array
 * @param options - Array of options
 * @param valueToAdd - Value to add if not already in the array
 * @returns Sorted array with the value added if it wasn't already present
 */
export const addValueToOptions = <T>(options: T[], valueToAdd: T | null | undefined): T[] => {
	if (valueToAdd === null || valueToAdd === undefined) {
		return [...options];
	}

	if (!options.includes(valueToAdd)) {
		return [...options, valueToAdd].sort((a: T, b: T) => {
			if (typeof a === 'number' && typeof b === 'number') {
				return a - b;
			}
			// Default string comparison for non-numeric types
			return String(a).localeCompare(String(b));
		});
	}

	return options;
};

/**
 * Filters pasted text to contain only numeric characters and limits to max length
 * @param pastedText - The text from clipboard
 * @param maxLength - Maximum number of characters to accept
 * @returns Filtered numeric string limited to maxLength
 * @example
 * filterNumericPaste('1234 5678 90', 10) // Returns '1234567890'
 * filterNumericPaste('KVK: 1234-5678', 8) // Returns '12345678'
 * filterNumericPaste('abc123def456', 5) // Returns '12345'
 */
export const filterNumericPaste = (pastedText: string, maxLength: number): string => {
	// Strip all non-numeric characters and limit to max length
	return pastedText.replace(/\D/g, '').slice(0, maxLength);
};

/**
 * Handles paste event for numeric-only input fields
 * Prevents default paste behavior and filters the pasted content
 * @param e - React clipboard event
 * @param fieldId - ID of the input field to update
 * @param maxLength - Maximum number of characters allowed
 * @param setFormData - State setter function for form data
 * @param clearError - Optional function to clear field errors
 * @example
 * handleNumericPaste(e, 'kvk', 10, setFormData, () => clearErrors('kvk'))
 */
export const handleNumericPaste = <T extends Record<string, unknown>>(
	e: React.ClipboardEvent<HTMLInputElement>,
	fieldId: string,
	maxLength: number,
	setFormData: React.Dispatch<React.SetStateAction<T>>,
	clearError?: () => void
): void => {
	const { id } = e.target as HTMLInputElement;
	if (id === fieldId) {
		e.preventDefault();
		const pastedText = e.clipboardData.getData('text');
		const numericValue = filterNumericPaste(pastedText, maxLength);

		setFormData(
			(prevState) =>
				({
					...prevState,
					[id]: numericValue
				}) as T
		);

		// Clear error if callback provided
		if (clearError) {
			clearError();
		}
	}
};
